import React from 'react';
import classnames from 'classnames';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import { Field, FieldTypes, DropdownField, RadioField } from 'hellospa/components/editor/types';
import { PendingTrigger } from './types';
import Checkbox from 'hellospa/components/checkbox';
import { Trigger, AllowedTriggerField } from 'hellospa/conditional-logic/types';
import * as Dropdown from 'hellospa/foundation/control/dropdown';
import BufferedInput from 'hellospa/components/buffered-input';
import styles from './rules-panel.scss';
import { useHtmlIDs } from 'hellospa/common/hooks';

/**
 * Populates extra fields based on trigger type and operator
 * @param triggerField field that is acting as the trigger
 * @param trigger the trigger that's part of the rule
 * @param onValidate function that handles validation and stores
 */
type Props = {
  triggerField: Field,
  trigger: PendingTrigger,
  allFields: Field[],
  onValidate: (
    triggerType: AllowedTriggerField,
    type: Trigger['operator'],
    trigger: PendingTrigger,
    value: any,
    name: any
  ) => void,
};

export const messages = defineMessages({
  textExactly: {
    description: 'error message in editor asking user to make merge field label name unique across all components',
    defaultMessage: 'Text exactly',
  },
  enterRegexExpression: {
    description: 'error message in editor asking user to make merge field label name unique across all components',
    defaultMessage: 'Enter a regular expression',
  },
});

function TriggerOperatorDropdownExtras({
  triggerField, trigger, onValidate, allFields,
}: Props): JSX.Element | null {

  switch (triggerField.type) {
    case FieldTypes.Text:
      return (
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        <TextInputs
          trigger={trigger}
          onSave={
          (value, name) => onValidate(triggerField.type, trigger.operator, trigger, value, name)
        } />
      );
    case FieldTypes.Dropdown:
      return (
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        <DropdownInputs
          trigger={trigger}
          triggerField={triggerField}
          onSave={
          (value, name) => onValidate(triggerField.type, trigger.operator, trigger, value, name)
        } />
      );
    case FieldTypes.Radio:
      return (
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        <RadioInputs
          trigger={trigger}
          triggerField={triggerField}
          allFields={allFields}
          onSave={
          (value, name) => onValidate(triggerField.type, trigger.operator, trigger, value, name)
        } />

      );
    default:
      return null;
  }
}

export default React.memo(TriggerOperatorDropdownExtras);

type TextInputsProps = {
  trigger: PendingTrigger,
  onSave: (value: string, name: string) => void
}
function TextInputs({
  trigger,
  onSave,
}: TextInputsProps) {
  const id = useHtmlIDs();
  const intl = useIntl();

  switch (trigger.operator) {
    case 'match':
      return (
        <div className={classnames(styles.operatorExtraInput, styles.regexContainer)}>
          /
          <BufferedInput
            className={styles.regexInput}
            type="text"
            id={id('text-value-input')}
            name="regex"
            placeholder={intl.formatMessage(messages.enterRegexExpression)}
            value={String(trigger.value || '')}
            data-qa-ref="regex-input"
            onSave={onSave} />
          /
        </div>
      );
    case 'is':
    case 'not':
      return (
        <BufferedInput
          className={styles.operatorExtraInput}
          type="text"
          id={id('text-value-input')}
          name="value"
          placeholder={intl.formatMessage(messages.textExactly)}
          value={String(trigger.value || '')}
          data-qa-ref="regex-input"
          onSave={onSave} />
      );

    default:
      return null;
  }
}

type DropdownInputsProps = {
  trigger: PendingTrigger,
  triggerField: DropdownField,
  onSave: (value: string | boolean, name: string) => void
}
function DropdownInputs({
  trigger,
  triggerField,
  onSave,
}: DropdownInputsProps) {
  switch (trigger.operator) {
    case 'is':
    case 'not': {
      const dropdownOptions: JSX.Element[] = triggerField.options
        .map((option: string, i: number) => {
          return (
            <Dropdown.MenuItem key={`${option}-${i}`} value={option} tag="li">
              {option}
            </Dropdown.MenuItem>
          );
        });
      return (
        <Dropdown.Wrapper
          onSelection={
            // name isn't use for the `is` and `not` operators
            (value) => onSave(value, '')
          }
          kind="small"
          className={styles.operatorExtraInput}
        >
          <Dropdown.Button data-qa-ref="rules-panel-option-select">
            {trigger.value
              || <FormattedMessage
                id=""
                description="dropdown text in editor asking user to select one the of the options from dropdown"
                defaultMessage="Select" />
            }
          </Dropdown.Button>
          <Dropdown.Menu tag="ul">
            {dropdownOptions}
          </Dropdown.Menu>
        </Dropdown.Wrapper>
      );
    }
    case 'any':
    case 'none': {
      const values: string[] = (trigger.value ? trigger.value : []) as string[];
      const dropdownOptions: JSX.Element[] = triggerField.options
        .map((option: string, i: number) => {
          return (
            <div key={`${option}-${i}`} className={styles.singleOption}>
              <Checkbox
                value={option}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  onSave(event.target.checked, option);
                }}
                checked={values.includes(option)}
                className={styles.fieldOptionCheckbox}
                indeterminate={false}
                id={`${option}-${i}`} />
              <label htmlFor={`${option}-${i}`}>
                {option}
              </label>
            </div>
          );
        });

      return (
        <div className={styles.allValuesBox}>
          {dropdownOptions}
        </div>
      );
    }
    default:
      return null;
  }
}

type RadioButtonOptions = {
  id: string,
  name: string,
};
type RadioInputsProps = {
  trigger: PendingTrigger,
  triggerField: RadioField,
  allFields: Field[]
  onSave: (value: string | boolean, name: string) => void
}
function RadioInputs({
  trigger,
  triggerField,
  allFields,
  onSave,
}: RadioInputsProps) {
  const selectedOperator = trigger.operator;
  // get all the radio button names/ids here
  const options = allFields
    .filter((f) => f.type === triggerField.type && f.group === triggerField.group)
    .reduce((vals, f) => {
      vals.push({ id: f.id, name: f.name || f.id });
      return vals;
    }, [] as Array<RadioButtonOptions>);

  if (selectedOperator === 'is' || selectedOperator === 'not') {
    const selectedValue = trigger.value
      ? options.filter((o) => o.id === trigger.value)[0].name
      : null;
    const dropdownOptions: JSX.Element[] = options
      .map((option: RadioButtonOptions, i: number) => {
        return (
          <Dropdown.MenuItem key={`${option.id}-${i}`} value={option.id} tag="li">
            {option.name}
          </Dropdown.MenuItem>
        );
      });
    return (
      <Dropdown.Wrapper
        onSelection={
            // name isn't use for the `is` and `not` operators
            (value) => onSave(value, '')
          }
        kind="small"
        className={styles.operatorExtraInput}
      >
        <Dropdown.Button data-qa-ref="rules-panel-option-select">
          {selectedValue
              || <FormattedMessage
                id=""
                description="dropdown text in editor asking user to select one the of the options from dropdown"
                defaultMessage="Select" />
            }
        </Dropdown.Button>
        <Dropdown.Menu tag="ul">
          {dropdownOptions}
        </Dropdown.Menu>
      </Dropdown.Wrapper>
    );
  } else if (selectedOperator === 'any' || selectedOperator === 'none') {
    const values: string[] = (trigger.value ? trigger.value : []) as string[];
    const dropdownOptions: JSX.Element[] = options
      .map((option: RadioButtonOptions, i: number) => {
        return (
          <div key={`${option.id}-${i}`} className={styles.singleOption}>
            <Checkbox
              value={option.id}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                onSave(event.target.checked, option.id);
              }}
              checked={values.includes(option.id)}
              className={styles.fieldOptionCheckbox}
              indeterminate={false}
              id={`${option.id}-${i}`} />
            <label htmlFor={`${option.id}-${i}`}>
              {option.name}
            </label>
          </div>
        );
      });

    return (
      <div className={styles.allValuesBox}>
        {dropdownOptions}
      </div>
    );
  }
  return null;
}
